/**
 * Tile layer URL presets for common map styles.
 * Import and use as `tileUrl` to switch between map themes.
 */
export const TILE_PRESETS = {
  /** CartoDB dark — default space ops aesthetic. */
  dark: "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
  /** Esri World Imagery — natural satellite photos, makes the terminator dramatically visible. */
  satellite:
    "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
  /** CartoDB Voyager — light/neutral with labels. */
  light:
    "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png",
} as const;

/** Primary basemap: CARTO dark with labels. */
export const DEFAULT_TILE =
  "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png";

export const CARTO_ATTRIBUTION =
  '&copy; <a href="https://carto.com/attribution" target="_blank" rel="noopener noreferrer">CARTO</a>';

/** Fallback when primary tiles fail to avoid black tiles. */
export const FALLBACK_TILE =
  "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

export const OSM_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap contributors</a>';

/**
 * Esri World Imagery — required when using tiles from ArcGIS Online MapServer.
 * @see https://developers.arcgis.com/documentation/mapping-apis-and-services/deployment/basemap-attribution/
 */
export const ESRI_WORLD_IMAGERY_ATTRIBUTION =
  'Tiles &copy; <a href="https://www.esri.com/" target="_blank" rel="noopener noreferrer">Esri</a> ' +
  "&mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community";

/**
 * Resolve HTML attribution for a basemap URL template (Leaflet `TileLayer` `attribution` option).
 * Unknown / custom URLs return an empty string — apps should document their own terms for those.
 */
export function getBasemapAttribution(tileUrl: string): string {
  const u = tileUrl.toLowerCase();
  if (u.includes("cartocdn")) return CARTO_ATTRIBUTION;
  if (u.includes("arcgisonline.com") || u.includes("arcgis.com"))
    return ESRI_WORLD_IMAGERY_ATTRIBUTION;
  if (u.includes("openstreetmap.org")) return OSM_ATTRIBUTION;
  return "";
}

/**
 * After this many consecutive tile errors, switch to the fallback layer.
 * Set to 1 so we bail out immediately on the first network failure instead
 * of waiting for several timeouts (which shows a black map for ~5 seconds).
 */
export const TILE_ERROR_THRESHOLD = 1;
